/**
 * 大数据埋点系统
 **/
import store from '../../store/index';
import config from '@/config/user.env';
import dayjs from 'dayjs';
import axios from 'axios';
import { RouteLocationNormalizedGeneric } from 'vue-router';
import { findMenuModelNames } from '@/composition/useStoreFunc';

interface baseObject {
  [key: string]: any;
}
// 使用接口定义参数结构
interface SendDetailMessageOptions {
  modelInfo?: baseObject;
  moduleName?: string;
  childModuleName?: string;
  href?: string;
  webSystem?: string;
  webVersion?: string;
}
declare const window: any;

const defaultConfig = {
  moduleName: 'C-EL',
};
// 配置标识转换
const mappingKey: baseObject = {
  oms: 'C-LOMS',
  tms: 'C-TMS',
};

class BigDataLog {
  environment: string;
  mainApi: string;
  detailApi: string;
  moduleName: string;
  childModuleName: string;
  userName: string;
  browserInfo: object;
  screenInfo: object;
  route: any;
  href: string;
  webSystem: string;
  webVersion: string;

  constructor() {
    this.environment = config.env || 'UAT';
    this.mainApi = config.bigDataLogUrl + '/api/2.0/send_main_message';
    this.detailApi = config.bigDataLogUrl + '/api/2.0/send_detail_message';
    this.moduleName = defaultConfig.moduleName;
    this.childModuleName = '';
    this.userName = '';
    this.browserInfo = {};
    this.screenInfo = {};
    this.route = {};
    this.href = '';
    this.webSystem = config.webSystem || 'EL';
    this.webVersion = config.webVersion || '';
  }

  ensureDeviceInfo() {
    if (!Object.keys(this.browserInfo as object).length) {
      this.browserInfo = this.getBrowserInfo();
      this.screenInfo = this.getScreenInfo();
    }
  }
  buildPostBody(topicName: string, message: baseObject) {
    return new URLSearchParams({
      appName: config.bigDataLogAppName || 'C-EL',
      topicName,
      message: JSON.stringify(message),
      // 与 LSP utils.ajax formatAjaxParams 一致，防缓存随机参数
      v: String(Math.random()).replace('.', ''),
    }).toString();
  }

  init(moduleName: string = defaultConfig.moduleName, childModuleName: string | undefined = '') {
    this.moduleName = moduleName;
    this.userName = store.state.user.userCode;
    this.childModuleName = childModuleName;
    this.browserInfo = this.getBrowserInfo();
    this.screenInfo = this.getScreenInfo();
  }
  getTime(date: Date | string | undefined | null = '') {
    return (date ? dayjs(date) : dayjs()).format('YYYY-MM-DD HH:mm:ss');
  }
  changeModuleName(name: string) {
    this.moduleName = name;
  }
  setModuleName(name: string | undefined = '') {
    if (name) {
      this.changeModuleName(mappingKey[name] || name);
    } else {
      const { route } = this;
      if (route && route.meta && route.meta.system) {
        this.changeModuleName(mappingKey[route.meta.system]);
      } else {
        this.changeModuleName(defaultConfig.moduleName);
      }
    }
  }
  getBrowserInfo() {
    const Sys: baseObject = {
      platform: window.navigator.platform,
      userAgent: window.navigator.userAgent,
      language: window.navigator.language,
      appPlatform: window.navigator.appVersion,
      appVersion: window.navigator.buildID,
    };
    const ua = window.navigator.userAgent.toLowerCase();
    const re = /(msie|firefox|chrome|opera|version).*?([\d.]+)/;
    const m = ua.match(re);
    if (m && m[0] && m[1]) {
      Sys.appCodeName = m[1].replace(/version/, `'safari`);
      Sys.appName = m[0];
    }
    return Sys;
  }
  getScreenInfo() {
    return {
      availWidth: window.screen.availWidth,
      availHeight: window.screen.availHeight,
      width: window.screen.width,
      height: window.screen.height,
      colorDepth: window.screen.colorDepth,
      pixelDepth: window.screen.pixelDepth,
    };
  }
  getNetworkType() {
    let netType = 'wifi';
    if (window.navigator.connection && window.navigator.connection.effectiveType) {
      netType = window.navigator.connection.effectiveType;
    }
    return netType;
  }
  getMainMessage() {
    return {
      environment: this.environment,
      userName: this.userName,
      moduleName: this.moduleName,
      childModuleName: this.childModuleName,
      clientDate: this.getTime(),
      href: window.location.href,
      ...this.screenInfo,
      ...this.browserInfo,
    };
  }
  getDetailMessage(modelInfo = {}, meta: { webSystem?: string; webVersion?: string } = {}) {
    this.ensureDeviceInfo();
    const webSystem = meta.webSystem ?? config.webSystem ?? 'EL';
    const webVersion = meta.webVersion ?? (webSystem === 'EL' ? config.webVersion || '' : '');
    return {
      environment: this.environment,
      userName: this.userName,
      moduleName: this.moduleName,
      childModuleName: this.childModuleName,
      clientDate: this.getTime(),
      href: this.href || window.location.href,
      webSystem,
      webVersion,
      ...this.screenInfo,
      ...this.browserInfo,
      ...modelInfo,
    };
  }
  getErrorMessage(errInfo: baseObject = {}) {
    return {
      environment: this.environment,
      userName: this.userName,
      moduleName: this.moduleName,
      clientDate: this.getTime(),
      href: window.location.href,
      netType: this.getNetworkType(),
      errContent: errInfo.Content,
      errType: errInfo.Type,
    };
  }
  sendMainMessage(childModuleName?: string) {
    // 必填信息不为空才提交
    if (!this.moduleName) {
      return false;
    }
    // 注意 userCode 在 store 存放的位置
    this.userName = store.getters.user.userCode;
    if (childModuleName !== undefined) {
      this.childModuleName = childModuleName;
    }
    this.ensureDeviceInfo();
    this.setModuleName();
    // 主日志完整 payload，大数据平台与火山引擎共用
    const modelInfo = this.getMainMessage();
    // 上报大数据平台主日志
    axios.post(
      this.mainApi,
      this.buildPostBody('request_data_main_log', modelInfo),
      {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
      },
    );
    // 同步上报火山引擎 DataRangers（window.collectEvent 在 index.html 初始化）
    window.collectEvent && window.collectEvent('request_data_main_log', modelInfo);
  }
  sendDetailMessage(messageOptions: SendDetailMessageOptions = {}) {
    // 必填信息不为空才提交
    if (!this.moduleName || !messageOptions?.modelInfo?.model_name1) {
      return false;
    }
    // 注意 userCode 在 store 存放的位置
    this.userName = store.getters.user.userCode;
    this.ensureDeviceInfo();
    if (messageOptions.childModuleName) {
      this.childModuleName = messageOptions.childModuleName;
    }
    if (messageOptions.href) {
      this.href = messageOptions.href;
    } else {
      this.href = window.location.href;
    }
    this.setModuleName(messageOptions.moduleName);
    // 明细业务字段（model_name1/2/3 等），火山引擎仅上报此部分，与 OTP bigDataLog 保持一致
    const modelInfo = messageOptions.modelInfo || {};
    // 上报大数据平台明细日志（含 userName、webSystem 等完整字段）
    axios.post(
      this.detailApi,
      this.buildPostBody(
        'request_data_detail_log',
        this.getDetailMessage(modelInfo, {
          webSystem: messageOptions.webSystem,
          webVersion: messageOptions.webVersion,
        }),
      ),
      {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
      },
    );
    // 同步上报火山引擎 DataRangers
    window.collectEvent && window.collectEvent('request_data_detail_log', modelInfo);
  }
  sendErrorMessage(errInfo: baseObject = {}) {
    // 必填信息不为空才提交
    if (!this.moduleName || !errInfo.errContent || !errInfo.errType) {
      return false;
    }
    this.setModuleName();
    axios.post(
      this.detailApi,
      this.buildPostBody('request_data_detail_log', this.getErrorMessage(errInfo)),
      {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
      },
    );
  }
  sendDetailMessageByKey(key: string) {
    this.setModuleName();
    let names = window.bigDataConfig[key];
    // 匹配不到埋点数据取消请求
    if (!names) {
      return false;
    }
    const params: baseObject = {};
    if (typeof names === 'string') {
      names = names.split(',');
    }
    names.forEach((name: string, index: number) => {
      params['model_name' + (index + 1)] = name;
    });
    this.sendDetailMessage(params);
  }
  sendDetailMessageOfRoute(fullPath: string) {
    const PAGE_TYPE = 2; // 统一权限 resourceType, 2指的是页面
    let modelName1 = '';
    let modelName2 = '';
    let matched = false;

    store.getters.menus.forEach((item: baseObject) => {
      if (item.resourceType === PAGE_TYPE) {
        modelName1 = item.frontUrl === fullPath ? item.resourceName : '';
        matched = true;
      } else {
        const matchedSubItem = item.subResources.find(
          (subItem: baseObject) => subItem.resourceType === PAGE_TYPE && subItem.frontUrl === fullPath,
        );
        if (matchedSubItem) {
          modelName1 = item.resourceName;
          modelName2 = matchedSubItem.resourceName;
          matched = true;
        }
      }
    });
    if (matched) {
      this.sendDetailMessage({
        modelInfo:{
          model_name1: modelName1,
          model_name2: modelName2,
        }
      });
    }
  }
  sendDetailMessageByRoute(route: RouteLocationNormalizedGeneric, childModuleName = '', trackMeta: { webSystem?: string; webVersion?: string } = {}) {
    const modelInfo = this.getModelInfoByRoute(route);
    if (Object.keys(modelInfo).length) {
      const baseUrl = window.location.origin + window.location.pathname;
      let completeUrl = '';
      if (baseUrl && route.fullPath) {
        completeUrl = `${baseUrl}#${route.fullPath}`;
      }
      this.sendDetailMessage({
        modelInfo: modelInfo,
        moduleName: 'C-EL',
        childModuleName: childModuleName,
        href: completeUrl,
        webSystem: trackMeta.webSystem,
        webVersion: trackMeta.webVersion,
      });
    }
  }
  fillModelInfoFromMenus(path: string, modelInfo: baseObject) {
    const menuNames = findMenuModelNames(path);
    if (!menuNames?.length) {
      return modelInfo;
    }
    menuNames.forEach((item: baseObject, index: number) => {
      modelInfo[`model_name${index + 1}`] = item.resourceName;
    });
    return modelInfo;
  }
  getModelInfoByRoute(route: RouteLocationNormalizedGeneric, options: { authorityOnly?: boolean } = {}) {
    if (!route.path) {
      return {};
    }
    const path = route.path.split('?')[0];
    const resources = store.getters.authority || store.state.authority;
    const infoMap = new Map<string, string>();
    const modelInfo: baseObject = {};
    if (resources && resources.length) {
      resources.forEach((item: baseObject) => {
        if (item.frontUrl === path) {
          infoMap.set('model_name1', item.resourceName);
          return false;
        }
        if (item.subResources && item.subResources.length) {
          item.subResources.forEach((subItem: baseObject) => {
            if (subItem.frontUrl === path) {
              infoMap.set('model_name1', item.resourceName);
              infoMap.set('model_name2', subItem.resourceName);
              return false;
            }
            if (subItem.subResources) {
              subItem.subResources.forEach((subItemSon: baseObject) => {
                if (subItemSon.frontUrl === path) {
                  infoMap.set('model_name1', item.resourceName);
                  infoMap.set('model_name2', subItem.resourceName);
                  infoMap.set('model_name3', subItemSon.resourceName);
                  return false;
                }
              });
            }
          });
        }
      });
      for (const [key, value] of infoMap) {
        modelInfo[key] = value;
      }
    }
    // 页面埋点与 OTP 一致仅匹配 authority；菜单 fallback 供其他场景使用
    if (!options.authorityOnly && !Object.keys(modelInfo).length) {
      this.fillModelInfoFromMenus(path, modelInfo);
    }
    return modelInfo;
  }
}
export default new BigDataLog();
